import { Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { POPUP_LEVEL } from '@/components/ui/popup-level';

const norm = (s: string) => s.trim().toLowerCase();

/**
 * Inline single-category control on a board card: the badge (or a "+ Category" affordance when
 * empty) opens a small combobox — filter existing categories or type a new one. Category is the
 * structural axis the board groups by, so setting it is one click. Popup is portalled so the
 * card's `overflow` / click-to-edit don't interfere. `onChange('')` clears the category.
 */
export default function CategoryPicker({ value, options, onChange }: {
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function openPop(e: React.MouseEvent) {
    e.stopPropagation();
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left });
    setInput('');
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    function onDown(e: MouseEvent) {
      if (!popRef.current?.contains(e.target as Node) && !triggerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onScroll() { setOpen(false); }
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    return () => { cancelAnimationFrame(raf); document.removeEventListener('mousedown', onDown); window.removeEventListener('scroll', onScroll, true); };
  }, [open]);

  function pick(v: string) { onChange(v.trim()); setOpen(false); }

  const trimmed = input.trim();
  const matches = trimmed ? options.filter((o) => norm(o).includes(norm(trimmed))) : options;
  const canCreate = !!trimmed && !options.some((o) => norm(o) === norm(trimmed));

  const pop = open && createPortal(
    <div ref={popRef} style={{ top: pos.top, left: pos.left }}
      className={`fixed ${POPUP_LEVEL} w-56 rounded-lg border border-ws-border bg-ws-elevated p-1 shadow-lg`}>
      <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Enter' && canCreate) { e.preventDefault(); pick(trimmed); } else if (e.key === 'Escape') setOpen(false); }}
        placeholder={t('experience.card.categoryPlaceholder')}
        className='mb-1 w-full rounded-md border border-ws-border bg-ws-control-pressed px-2 py-1.5 text-xs text-ws-text placeholder:text-ws-muted focus:outline-none' />
      <div className='max-h-52 overflow-y-auto'>
        {value && (
          <button onClick={(e) => { e.stopPropagation(); pick(''); }} className='flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs text-ws-muted hover:bg-ws-hover'>
            <X className='h-3 w-3 shrink-0' />{t('experience.card.noCategory')}
          </button>
        )}
        {matches.map((o) => (
          <button key={o} onClick={(e) => { e.stopPropagation(); pick(o); }} className={`flex w-full items-center rounded-md px-2 py-1.5 text-left text-xs hover:bg-ws-hover ${norm(o) === norm(value) ? 'font-medium text-ws-text-link' : 'text-ws-text'}`}>{o}</button>
        ))}
        {canCreate && (
          <button onClick={(e) => { e.stopPropagation(); pick(trimmed); }} className='flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs text-ws-text hover:bg-ws-hover'>
            <Plus className='h-3 w-3 shrink-0' /><span className='truncate'>{trimmed}</span>
          </button>
        )}
      </div>
    </div>,
    document.body,
  );

  return (
    <>
      {value ? (
        <button ref={triggerRef} onClick={openPop} title={value}
          className='rounded border border-ws-text-link/50 px-2 py-0.5 text-sm font-medium leading-none text-ws-text-link hover:bg-ws-hover'>
          {value}
        </button>
      ) : (
        <button ref={triggerRef} onClick={openPop}
          className='flex items-center gap-1 rounded border border-dashed border-ws-border px-2 py-0.5 text-sm leading-none text-ws-muted hover:bg-ws-hover hover:text-ws-text'>
          <Plus className='h-3.5 w-3.5' />{t('experience.card.category')}
        </button>
      )}
      {pop}
    </>
  );
}
